/** Mensagens exatas retornadas pela API (Xano, etc.). */
const EXACT_TRANSLATIONS: Record<string, string> = {
  'Slug already exists': 'Este slug já está em uso.',
  'Missing param: email': 'Informe o e-mail.',
  'Email already exists': 'Este e-mail já está cadastrado.',
  'User already exists': 'Este usuário já existe.',
  'User already internal': 'Este usuário já possui acesso interno.',
  'User not found': 'Usuário não encontrado.',
  'Email does not match user_id': 'O e-mail não confere com o usuário informado.',
  'Input does not meet minimum length requirement of 8 characters':
    'Um campo precisa ter pelo menos 8 caracteres.',
  'Not Found': 'Recurso não encontrado.',
  Unauthorized: 'Não autorizado. Faça login novamente.',
  Forbidden: 'Sem permissão para esta ação.',
  'Internal user is disabled':
    'Acesso ao backoffice desativado para esta conta.',
  'Invalid Credentials': 'E-mail ou senha incorretos.',
  'Invalid Credential': 'E-mail ou senha incorretos.',
  'Bad Request': 'Não foi possível concluir a solicitação. Verifique os dados e tente de novo.',
  'Internal Server Error': 'Erro interno do servidor. Tente novamente.',
  'Too Many Requests': 'Muitas tentativas. Aguarde um momento e tente de novo.',
};

/** Padrões parciais (mensagem contém o texto). */
const PARTIAL_TRANSLATIONS: Array<{ match: string; text: string }> = [
  { match: 'slug already exists', text: 'Este slug já está em uso.' },
  { match: 'missing param: email', text: 'Informe o e-mail.' },
  { match: 'email already exists', text: 'Este e-mail já está cadastrado.' },
  { match: 'user already internal', text: 'Este usuário já possui acesso interno.' },
  { match: 'email does not match', text: 'O e-mail não confere com o usuário informado.' },
  { match: 'minimum length requirement of 8', text: 'Um campo precisa ter pelo menos 8 caracteres.' },
  { match: 'duplicate', text: 'Registro duplicado — este valor já existe.' },
  { match: 'not found', text: 'Recurso não encontrado.' },
  { match: 'unauthorized', text: 'Não autorizado. Faça login novamente.' },
  { match: 'forbidden', text: 'Sem permissão para esta ação.' },
  {
    match: 'internal user is disabled',
    text: 'Acesso ao backoffice desativado para esta conta.',
  },
];

const PARAM_LABELS: Record<string, string> = {
  email: 'o e-mail',
  password: 'a senha',
  new_password: 'a nova senha',
  current_password: 'a senha atual',
  phone: 'o telefone',
  first_name: 'o nome',
  last_name: 'o sobrenome',
  cpf: 'o CPF',
  cnpj: 'o CNPJ',
  company_name: 'o nome da empresa',
  company: 'o nome da empresa',
  token: 'o código',
  name: 'o nome',
  code: 'o código',
};

const HTTP_STATUS_TEXT: Record<string, string> = {
  'bad request':
    'Não foi possível concluir a solicitação. Verifique os dados e tente de novo.',
  unauthorized: 'Não autorizado. Faça login novamente.',
  forbidden: 'Sem permissão para esta ação.',
  'not found': 'Recurso não encontrado.',
  conflict: 'Este registro já existe.',
  'unprocessable entity': 'Dados inválidos. Revise os campos e tente de novo.',
  'unprocessable content': 'Dados inválidos. Revise os campos e tente de novo.',
  'too many requests': 'Muitas tentativas. Aguarde um momento e tente de novo.',
  'internal server error': 'Erro interno do servidor. Tente novamente.',
  'bad gateway': 'Serviço indisponível no momento. Tente novamente.',
  'service unavailable': 'Serviço indisponível no momento. Tente novamente.',
  'gateway timeout': 'O serviço demorou para responder. Tente novamente.',
};

const COUNT_WORDS: Record<string, number> = {
  a: 1,
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
};

type PasswordRule = {
  re: RegExp;
  format: (count: number) => string;
};

const PASSWORD_RULES: PasswordRule[] = [
  {
    re: /(\d+|a|an|one|two|three|four)\s+special characters?/gi,
    format: count =>
      count === 1 ? '1 caractere especial' : `${count} caracteres especiais`,
  },
  {
    re: /(\d+|a|an|one|two|three|four)\s+symbols?/gi,
    format: count =>
      count === 1 ? '1 caractere especial' : `${count} caracteres especiais`,
  },
  {
    re: /(\d+|a|an|one|two|three|four)\s+uppercase (?:letters?|characters?)/gi,
    format: count =>
      count === 1 ? '1 letra maiúscula' : `${count} letras maiúsculas`,
  },
  {
    re: /(\d+|a|an|one|two|three|four)\s+lowercase (?:letters?|characters?)/gi,
    format: count =>
      count === 1 ? '1 letra minúscula' : `${count} letras minúsculas`,
  },
  {
    re: /(\d+|a|an|one|two|three|four)\s+(?:numbers?|digits?)/gi,
    format: count => (count === 1 ? '1 número' : `${count} números`),
  },
  {
    re: /(\d+|a|an|one|two|three|four)\s+letters?/gi,
    format: count => (count === 1 ? '1 letra' : `${count} letras`),
  },
  {
    re: /(\d+|a|an|one|two|three|four)\s+characters?/gi,
    format: count => (count === 1 ? '1 caractere' : `${count} caracteres`),
  },
];

function parseCount(raw: string): number {
  const word = COUNT_WORDS[raw.toLowerCase()];
  if (word) return word;
  const count = Number(raw);
  return Number.isFinite(count) ? count : 1;
}

function joinPt(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  if (parts.length === 2) return `${parts[0]} e ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
}

function unwrapApiText(message: string): string {
  const trimmed = message.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return trimmed;

  try {
    const data = JSON.parse(trimmed) as { message?: unknown; error?: unknown };
    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message.trim();
    }
    if (typeof data?.error === 'string' && data.error.trim()) {
      return data.error.trim();
    }
  } catch {
    // corpo que não é JSON
  }

  return trimmed;
}

function extractPasswordRules(message: string): string[] {
  let cursor = message;
  const found: Array<{ index: number; text: string }> = [];

  for (const rule of PASSWORD_RULES) {
    rule.re.lastIndex = 0;
    cursor = cursor.replace(rule.re, (full, count: string, offset: number) => {
      found.push({ index: offset, text: rule.format(parseCount(count)) });
      return ' '.repeat(full.length);
    });
  }

  return found.sort((a, b) => a.index - b.index).map(item => item.text);
}

/** O Xano devolve a política de senha em inglês, inclusive com plural errado ("1 numbers"). */
function translatePasswordMessage(message: string): string | null {
  const lower = message.toLowerCase();
  const isWeak =
    lower.includes('weak password') ||
    lower.includes('password does not meet') ||
    lower.includes('password must') ||
    lower.includes('password is too');
  const rules = extractPasswordRules(message);

  if (
    lower.includes('data breach') ||
    lower.includes('too common') ||
    lower.includes('commonly used')
  ) {
    return 'Esta senha é muito comum. Escolha outra.';
  }

  if (rules.length === 0) {
    if (!isWeak) return null;
    return 'Senha fraca. Use uma combinação de letras, números e caracteres especiais.';
  }

  if (!isWeak && !lower.includes('please use at least') && !lower.includes('password')) {
    return null;
  }

  return `Senha fraca. Use pelo menos ${joinPt(rules)}.`;
}

function translateLengthRequirement(message: string): string | null {
  const minimum = message.match(/minimum length requirement of (\d+)/i);
  if (minimum) {
    const count = minimum[1];
    if (/password|senha/i.test(message)) {
      return `A senha deve ter pelo menos ${count} caracteres.`;
    }
    return `Um campo precisa ter pelo menos ${count} caracteres.`;
  }

  const maximum = message.match(/maximum length requirement of (\d+)/i);
  if (maximum) {
    return `Um campo pode ter no máximo ${maximum[1]} caracteres.`;
  }

  return null;
}

function translateNamedParam(message: string): string | null {
  const missing = message.match(/missing param:?\s*([a-z0-9_]+)/i);
  if (missing) {
    const label = PARAM_LABELS[missing[1].toLowerCase()];
    return label ? `Informe ${label}.` : 'Preencha os campos obrigatórios.';
  }

  const invalid = message.match(
    /invalid (?:value|param)(?::|\s+for(?:\s+param)?:?)?\s*([a-z0-9_]+)/i
  );
  if (invalid) {
    const label = PARAM_LABELS[invalid[1].toLowerCase()];
    return label
      ? `Revise ${label}.`
      : 'Revise os dados informados e tente novamente.';
  }

  return null;
}

function translateKnownPhrase(message: string): string | null {
  const rules: Array<{ re: RegExp; text: string }> = [
    {
      re: /(?:invalid|wrong|incorrect) credentials?|credentials? (?:are |is )?(?:invalid|wrong|incorrect)/i,
      text: 'E-mail ou senha incorretos.',
    },
    { re: /access[_\s]denied/i, text: 'Sem permissão para esta ação.' },
    { re: /authentication required/i, text: 'Faça login para continuar.' },
    {
      re: /unable to locate auth token|invalid (?:auth )?token|token (?:is |has )?expired|expired token|jwt expired|signature (?:has |is )?expired/i,
      text: 'Sua sessão expirou. Faça login novamente.',
    },
    {
      re: /not a valid email|invalid email/i,
      text: 'E-mail inválido.',
    },
    { re: /not a valid phone|invalid phone/i, text: 'Telefone inválido.' },
    {
      re: /passwords? do not match|password confirmation (?:does not match|mismatch)/i,
      text: 'As senhas não coincidem.',
    },
    {
      re: /(?:incorrect|wrong|invalid) password|password is (?:incorrect|wrong|invalid)/i,
      text: 'Senha incorreta.',
    },
    {
      re: /too many requests|rate limit/i,
      text: 'Muitas tentativas. Aguarde um momento e tente de novo.',
    },
    {
      re: /internal user is disabled/i,
      text: 'Acesso ao backoffice desativado para esta conta.',
    },
    {
      re: /account (?:is |has been )?disabled|user is disabled|this account has been disabled/i,
      text: 'Esta conta está desativada.',
    },
    {
      re: /unable to locate (?:this )?record|record not found/i,
      text: 'Registro não encontrado.',
    },
    {
      re: /invalid (?:verification |reset )?code|code is invalid|invalid or expired code/i,
      text: 'Código inválido ou expirado.',
    },
    {
      re: /email (?:address )?(?:is )?already (?:in use|registered|taken|exists)|this email is already/i,
      text: 'Este e-mail já está cadastrado.',
    },
    {
      re: /user already exists|username (?:is )?already/i,
      text: 'Este usuário já existe.',
    },
    {
      re: /already in use|already been taken|already exists/i,
      text: 'Este valor já está em uso.',
    },
    {
      re: /does not match (?:the )?(?:expected )?pattern|invalid format|does not match the required format/i,
      text: 'O formato informado é inválido.',
    },
    {
      re: /internal server error/i,
      text: 'Erro interno do servidor. Tente novamente.',
    },
    { re: /something went wrong/i, text: 'Algo deu errado. Tente novamente.' },
    {
      re: /failed to fetch|network error|load failed|networkerror/i,
      text: 'Erro de conexão. Verifique sua internet.',
    },
    {
      re: /is required|field is required|param(?:eter)? .+ is required/i,
      text: 'Preencha os campos obrigatórios.',
    },
  ];

  for (const rule of rules) {
    if (rule.re.test(message)) return rule.text;
  }

  return null;
}

function translateHttpStatus(message: string): string | null {
  const withCode = message.match(/^(?:erro\s+)?(\d{3})\s+(.+)$/i);
  const phrase = (withCode ? withCode[2] : message)
    .replace(/[.!]+$/, '')
    .trim()
    .toLowerCase();
  return HTTP_STATUS_TEXT[phrase] ?? null;
}

export function translateApiMessage(message: string): string {
  const unwrapped = unwrapApiText(message);
  const trimmed = unwrapped.replace(/\s+/g, ' ').trim();
  if (!trimmed) return message;

  const withoutPunctuation = trimmed.replace(/[.!]+$/, '');
  const exact = Object.entries(EXACT_TRANSLATIONS).find(
    ([key]) => key.toLowerCase() === withoutPunctuation.toLowerCase()
  );
  if (exact) return exact[1];

  const password = translatePasswordMessage(trimmed);
  if (password) return password;

  const length = translateLengthRequirement(trimmed);
  if (length) return length;

  const namedParam = translateNamedParam(trimmed);
  if (namedParam) return namedParam;

  const known = translateKnownPhrase(trimmed);
  if (known) return known;

  const httpStatus = translateHttpStatus(withoutPunctuation);
  if (httpStatus) return httpStatus;

  const lower = trimmed.toLowerCase();
  for (const { match, text } of PARTIAL_TRANSLATIONS) {
    if (lower.includes(match)) return text;
  }

  return trimmed;
}

export function userFacingError(error: unknown, fallback: string): string {
  let raw = '';
  if (error instanceof Error) {
    raw = error.message;
  } else if (error && typeof error === 'object' && 'message' in error) {
    raw = String((error as { message?: unknown }).message ?? '');
  }

  return translateApiMessage(raw.trim() || fallback);
}
